import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';

import './normalize.css';

import Banner from './components/Banner';
import { balls } from './utils/getBall';
import { postData } from './utils/postData';
import compressFiles from './utils/compressImages';
import { ApparelContext } from './context/FlowContext';
import Form from './steps/Form';
import SelectSport from './steps/SelectSport';
import NumDesigns from './steps/NumDesigns';
import JerseyType from './steps/JerseyType';
import DesignNumber from './steps/DesignNumber';
import SelectColor from './steps/SelectColor';
import SelectFont from './steps/SelectFont';
import CommentsAndUpload from './steps/CommentsAndUpload';

import { useForm } from './hooks/useForm';
import { defaultForm, formValidation } from './constants/formSetup';
import DesignStatus from './components/DesignStatus';
import ThankYou from './steps/ThankYou';
import {
  sendIframeHeight,
  sendIframeScrollToTop,
  sendDesignRequestSuccess,
} from './utils/iframePostMessage';
import AnotherDesignOption from './steps/AnotherDesignOption';
import SelectVariant from './steps/SelectVariant';
import ApparelType from './steps/ApparelType';

let totalFileSizeSubmitted = 0;

const steps = [
  Form,
  SelectSport,
  SelectColor,
  NumDesigns,
  JerseyType,
  DesignNumber,
  SelectFont,
  CommentsAndUpload,
];

const boaSteps = [
  Form,
  SelectSport,
  SelectColor,
  NumDesigns,
  JerseyType,
  DesignNumber,
  CommentsAndUpload,
];

const apparelSteps = [
  Form,
  SelectColor,
  ApparelType,
  DesignNumber,
  SelectVariant,
  SelectFont,
  AnotherDesignOption,
  CommentsAndUpload,
];

const numPlayers = {
  hockey: 'Bulk order for my team (7 or more)',
  baseball: 'Bulk order for my team (9 or more)',
  greek: 'Bulk order for my organization (7 or more)',
  soccer: 'Bulk order for my team (10 or more)',
  basketball: 'Bulk order for my team (9 or more)',
  softball: 'Bulk order for my team (9 or more)',
  rugby: 'For my team (7 or more jerseys)',
  apparel: 'Bulk order for my team (7 or more)',
};

const App = ({ envParam, variant }) => {

  const url = window.location.href;
  if (url.includes('localhost')) {
    const html = document.getElementsByTagName('html')[0];
    html.style.overflowY = 'scroll';
  }

  const [currentStep, setCurrentStep] = useState(0);
  const [formStatus, setFormStatus] = useState('ready');
  // Synchronous guard so rapid double-clicks can't submit twice before re-render
  const submittingRef = useRef(false);

  const { apparelFlow } = useContext(ApparelContext);

  useEffect(() => {
    sendIframeHeight();
    sendIframeScrollToTop();
  }, [currentStep]);

  const searchParams = new URLSearchParams(window.location.search);
  const source = searchParams.get('source');
  const sport = searchParams.get('sport');

  const initialValues = defaultForm;
  if (source) {
    initialValues.source = source;
  }

  if (envParam === 'apparelRequest' || sport === 'apparel') {
    initialValues.sport = 'apparel';
    initialValues.jerseyType = [];
  }

  if (variant === 'boa') {
    formValidation.numOrders.required.value = false;
  }

  const { triggerValidation, handleChange, data, errors } = useForm({
    mode: 'onBlur',
    validations: formValidation,
    initialValues,
  });

  const prevStep = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentStep(currentStep - 1);
  };

  const nextStep = (step = null) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const lastIndex =
      (envParam === 'apparel' ? apparelSteps : variant === 'boa' ? boaSteps : steps).length - 1;
    if (typeof step === 'number') {
      const target = Math.min(Math.max(0, step), lastIndex);
      setCurrentStep(target);
    } else {
      setCurrentStep(prev => Math.min(prev + 1, lastIndex));
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current || formStatus === 'submitting') return;
    submittingRef.current = true;
    setFormStatus('submitting');
    if (data.numOrders === 'Bulk') {
      data.numOrders = numPlayers[data.sport];
    }

    // Check if variant is in the list of variants that should set sport
    const variantSportMapping = ['boa', 'boa2', 'hrd', 'warstic'];
    if (variant && variantSportMapping.includes(variant)) {
      data.sport = variant;
    }


    const dataToSubmit = { ...data };

    if (apparelFlow.length > 0) {
      dataToSubmit.selectedApparel = apparelFlow;
      dataToSubmit.numDesigns = dataToSubmit.designNumbers.length;
      dataToSubmit.jerseyType = null;
    }

    try {
      const fd = new FormData();
      // Convert FileList to Array
      const designFilesArray = Array.from(data.designFiles);
      const { validFiles, totalFileSize } = await compressFiles(
        designFilesArray
      );
      totalFileSizeSubmitted = totalFileSize;

      Array.from(validFiles).forEach(file => {
        fd.append('files', file);
      });
      fd.append('data', JSON.stringify(dataToSubmit));
      const results = await postData(
        'https://triton-order-flow-api.vercel.app/api/submit',
        fd
      );

      if (
        results?.message?.toLowerCase().includes('success') ||
        results?.newRows?.message?.toLowerCase().includes('success')
      ) {
        setFormStatus('success');
        sendIframeScrollToTop();
        nextStep();
        if (typeof results.submissionId === 'string') {
          sendDesignRequestSuccess(dataToSubmit.sport, results.submissionId);
        }
      } else {
        submittingRef.current = false;
        setFormStatus('ready');
        alert('Something went wrong, please try again.');
      }
    } catch (e) {
      console.log('error: ', e);
      submittingRef.current = false;
      setFormStatus('ready');

      const errorMessage = `Error submitting form: ${e.message}`;
      // Send error email
      await fetch(
        'https://triton-order-flow-api.vercel.app/api/error-notification',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            error: errorMessage,
            formData: data,
          }),
        }
      ).catch(emailErr =>
        console.error('Failed to send error email:', emailErr)
      );

      alert(
        totalFileSizeSubmitted > 4500000
          ? '\nYour logo/photo attachments are too large. Please resize or compress your logo/photo files and resubmit your design request. \n\n If you are unable to do so, remove the logo/photo files, re-submit your design request, and email the files directly to us at sales@teamtriton.com.'
          : 'Something went wrong, please try again.\n\n'
      );
    }
  };

  const activeSteps = envParam === 'apparel' ? apparelSteps : variant === 'boa' ? boaSteps : steps;

  const Component = activeSteps[currentStep];

  return (
    <OrderFlowContainer variant={variant}>
      <Banner
        totalSteps={activeSteps.length}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        variant={variant}
      />
      {formStatus === 'success' ? (
        <ThankYou formData={data} variant={variant} />
      ) : (
        <>
          {/* <AnimatePresence initial={false} exitBeforeEnter> */}
          {formStatus !== 'ready' && <FormCover />}
          <Component
            key={currentStep}
            prevStep={prevStep}
            nextStep={nextStep}
            formData={data}
            errors={errors}
            triggerValidation={triggerValidation}
            handleChange={handleChange}
            envParam={envParam}
            variant={variant}
            setCurrentStep={setCurrentStep}
          />
          {/* </AnimatePresence> */}
          {currentStep === activeSteps.length - 1 && (
            <>
              <button
                className="submit-btn top-submit-btn"
                onClick={handleSubmit}
                disabled={formStatus === 'submitting'}
              >
                {formStatus === 'submitting' ? (
                  <>
                    {balls[data.sport]}
                    <p>Submitting...</p>
                  </>
                ) : (
                  <p>Submit Request</p>
                )}
              </button>
              <button
                className="submit-btn"
                onClick={handleSubmit}
                disabled={formStatus === 'submitting'}
              >
                {formStatus === 'submitting' ? (
                  <>
                    {balls[data.sport]}
                    <p>Submitting...</p>
                  </>
                ) : (
                  <p>Submit</p>
                )}
              </button>
            </>
          )}
          {((envParam === 'apparel' && currentStep === 3) ||
            (envParam !== 'apparel' && currentStep > 3 && currentStep < 6)) && (
            <DesignStatus
              nextStep={nextStep}
              prevStep={prevStep}
              currentStep={currentStep}
              designNumbers={data.designNumbers}
              totalDesigns={data.numDesigns}
              isApparel={envParam === 'apparel'}
            />
          )}
        </>
      )}
    </OrderFlowContainer>
  );
};

const OrderFlowContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  --red: ${props => props.variant === 'warstic' ? '#B29E6A' : '#c91102'};

  .submit-btn {
    background: var(--red);
    border: none;
    width: 100%;
    height: 100px;
    color: #fff;
    font-size: 1.2rem;
    text-transform: uppercase;
    cursor: pointer;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 1;
    z-index: 10;
    transition: all 0.15s ease-in-out;

    &.top-submit-btn {
      position: absolute;
      top: 235px;
      right: 0;
      width: 250px;
      height: 75px;
      border-top-left-radius: 5px;
      border-bottom-left-radius: 5px;

      @media (max-width: 767px) {
        border-bottom-left-radius: unset;
        border-top-right-radius: 5px;
        transform: rotate(-90deg);
        right: -77px;
        top: 350px;
        height: 45px;
        width: 200px;

        p {
          font-size: 22px !important;
        }
      }
    }

    p {
      font-size: 30px !important;
      letter-spacing: 1.75px;
      line-height: normal;
    }

    &:hover {
      background: #ff4f51;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    svg {
      width: 40px;
      height: 40px;
      margin-right: 10px;
      fill: #fff;
      animation: spin 1.5s linear infinite;
    }
  }
`;

const FormCover = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(200, 200, 200, 0.4);
  z-index: 100;
`;

export default App;
